import {
  InternalRefetchQueryDescriptor,
  useApolloClient,
} from "@apollo/client";
import { getDataOrNull } from "@msys/common";
import { InfoOutlined as InfoOutlinedIcon } from "@mui/icons-material";
import { IconButton, Stack } from "@mui/material";
import { useTranslate } from "@tolgee/react";
import { useSnackbar } from "notistack";
import React from "react";
import { CollectionView } from "../../../commons/hooks/useCollectionView.js";
import { ProductListItem } from "../../products/components/ProductItem.js";
import { ProductSearchContent } from "../../products/components/ProductSearchContent.js";
import { definitionsToFormValues } from "../../products/helper.js";
import { ProductOverviewModal } from "../../products/modals/ProductOverviewModal.js";
import { ProductSearchItem__ProductSearchResultFragment } from "../../products/Product.generated.js";
import { useAvailabilityWarning } from "../../quotes/alerts/QuoteProductsAvailabilityAlert.js";
import { useProductBox_SetItemProductMutation } from "../boxes/ProductBox.generated.js";
import {
  PLACEHOLDER_SELECTION_PAGE_SIZE,
  PLACEHOLDER_SELECTION_PAGE_SIZE_OPTIONS,
} from "./constants.js";
import {
  CURRENT_SELECTION_ROW_SX,
  CurrentSelectionChip,
} from "./CurrentSelectionChip.js";
import {
  ProductPlaceholderSelectionPanel_ItemFragment,
  useProductPlaceholderSelectionPanel_CatalogueProductQuery,
} from "./ProductPlaceholderSelectionPanel.generated.js";

const ALLOWED_VIEWS: CollectionView[] = ["list", "gallery", "table"];

// The pinned row is the placeholder's current product, not a search result:
// it has no select action, so it must not be found as one.
export const CURRENT_PRODUCT_ROW_TEST_ID =
  "product-placeholder-current-product";

interface Props {
  projectId: string | null;
  docId: string;
  item: ProductPlaceholderSelectionPanel_ItemFragment;
  expandedItemIds: string[] | undefined;
  onUpdateDataRefetchQueries?: InternalRefetchQueryDescriptor[];
  refetchWizardQuestions: () => Promise<unknown>;
  handleClose: () => void;
  /**
   * The placeholder was filled. Distinct from `handleClose`, which the search
   * surface also calls on a plain dismiss: a surface that steps on an answer
   * needs the two apart.
   */
  onApplied?: () => Promise<void>;
}

export const ProductPlaceholderSelectionPanel = ({
  projectId,
  docId,
  item,
  expandedItemIds,
  onUpdateDataRefetchQueries,
  refetchWizardQuestions,
  handleClose,
  onApplied,
}: Props) => {
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslate("Product");
  const client = useApolloClient();

  const doProductAvailabilityWarning = useAvailabilityWarning();

  const [setItemProduct] = useProductBox_SetItemProductMutation({
    client,
    refetchQueries: onUpdateDataRefetchQueries,
  });

  const initialFilters = React.useMemo(() => {
    const filters = definitionsToFormValues(item.productSearchFilterDefaults);
    return item.productPlaceholder
      ? { ...filters, productTypeIds: [item.productPlaceholder.productTypeId] }
      : filters;
  }, [item.productSearchFilterDefaults, item.productPlaceholder]);

  // Marks the product currently set on this placeholder, when it is among the
  // results. Both parts are needed — article numbers repeat across suppliers.
  const selectedProduct = React.useMemo(() => {
    const { articleNumber, supplierId } = item.product ?? {};
    return articleNumber && supplierId ? { articleNumber, supplierId } : null;
  }, [item.product]);

  const catalogueQuery =
    useProductPlaceholderSelectionPanel_CatalogueProductQuery({
      client,
      variables: selectedProduct ?? { articleNumber: "", supplierId: "" },
      skip: !selectedProduct,
    });
  const catalogueProduct = getDataOrNull(
    catalogueQuery.data?.pimGetProduct
  )?.product;
  const [inspectingCurrent, setInspectingCurrent] = React.useState(false);
  const CurrentProductInfoButton = React.useCallback(
    () => (
      <IconButton
        aria-label={t("Product details")}
        onClick={event => {
          event.stopPropagation();
          setInspectingCurrent(true);
        }}
      >
        <InfoOutlinedIcon color="secondary" />
      </IconButton>
    ),
    [t]
  );

  // Ignores repeat picks until the fill, the wizard refetch and the step have settled.
  const applying = React.useRef(false);
  const handleProductChoice = async (
    product: Pick<
      ProductSearchItem__ProductSearchResultFragment,
      "articleNumber" | "supplierId" | "status"
    >
  ) => {
    if (applying.current) return;
    applying.current = true;
    try {
      await setItemProduct({
        variables: {
          input: {
            projectId,
            docId,
            itemId: item.id,
            productArticleNumber: product.articleNumber,
            productSupplierId: product.supplierId,
            expandedItemIds,
          },
        },
      });

      if (product.status && product.status.value !== "available") {
        doProductAvailabilityWarning(product.status.value);
      }

      await refetchWizardQuestions();
      await onApplied?.();
      handleClose();
    } catch (error) {
      if (error instanceof Error)
        enqueueSnackbar(error.message, { variant: "error" });
    } finally {
      applying.current = false;
    }
  };

  return (
    <Stack direction="column" spacing={2}>
      {selectedProduct && catalogueProduct && (
        <>
          <ProductListItem
            product={catalogueProduct}
            ActionButton={CurrentProductInfoButton}
            selected
            StatusIcons={[<CurrentSelectionChip key="current-selection" />]}
            sx={CURRENT_SELECTION_ROW_SX}
            testId={CURRENT_PRODUCT_ROW_TEST_ID}
          />
          {inspectingCurrent && (
            <ProductOverviewModal
              productArticleNumber={selectedProduct.articleNumber}
              productSupplierId={selectedProduct.supplierId}
              handleClose={() => setInspectingCurrent(false)}
            />
          )}
        </>
      )}
      <ProductSearchContent
        initialFilters={initialFilters}
        handleProductChoice={handleProductChoice}
        handleClose={handleClose}
        canChangeProductType={!item.productPlaceholder}
        enableLandingPage={false}
        edgeBleed={false}
        initialPageSize={PLACEHOLDER_SELECTION_PAGE_SIZE}
        pageSizeOptions={PLACEHOLDER_SELECTION_PAGE_SIZE_OPTIONS}
        allowedViews={ALLOWED_VIEWS}
        collectionViewPersistenceKey="product-placeholder-selection"
        selectedProduct={selectedProduct}
        includePreferences
      />
    </Stack>
  );
};
